// Attaching a screenshot by pasting it (issue #739).
//
// Evidence for a non-conformance is almost always a screenshot — of the
// production data, a test report, a defect. Making someone save it to disk first
// just to pick it back up in a file dialog is a step that does not need to
// exist; the feedback widget already lets you paste one straight in.
//
// A pasted image arrives with no filename, so one is generated. Without it the
// upload lands in Cloudinary as "image.png" every time, and an NCR with three
// pasted screenshots would show three identically named attachments.

let pasteCounter = 0;

const EXTENSION_BY_TYPE = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
};

function namedCopy(file) {
  // Clipboard images are named "image.png" by the browser, or nothing at all.
  const looksUnnamed = !file.name || /^image\.\w+$/i.test(file.name);
  if (!looksUnnamed) return file;

  const ext = EXTENSION_BY_TYPE[file.type] || 'png';
  pasteCounter += 1;
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, '');
  return new File([file], `pasted-${stamp}-${pasteCounter}.${ext}`, {
    type: file.type,
    lastModified: file.lastModified,
  });
}

/**
 * The image files on a paste event, renamed so they are distinguishable.
 * Returns an empty array for a normal text paste, which must be left alone.
 */
export function imagesFromClipboard(event) {
  const items = event.clipboardData && event.clipboardData.items;
  if (!items) return [];

  const files = [];
  for (const item of items) {
    if (item.kind === 'file' && String(item.type).startsWith('image/')) {
      const file = item.getAsFile();
      if (file) files.push(namedCopy(file));
    }
  }
  return files;
}

/**
 * Paste handler that appends any pasted images to a file-list state setter.
 *
 * Appends rather than replaces: choosing files in the native input replaces the
 * selection (that is what the control does), but pasting is additive — you paste
 * a second screenshot expecting to have two.
 */
export function handleImagePaste(event, setFiles) {
  const pasted = imagesFromClipboard(event);
  if (!pasted.length) return false;
  event.preventDefault();
  setFiles(prev => [...(Array.isArray(prev) ? prev : []), ...pasted]);
  return true;
}
